import { useEffect, useRef, memo } from 'react';

import useProductStore from '@/store/product';

function InventoryComponents({ data }) {
  const isOversea = data?.isOversea;
  const productRecord = data?.productRecord;

  const { productAsyncData } = useProductStore();

  const StockTableDomRenderer = ({
    isSingleStock,
    isShowSmtSubsidyInfo,
    isShowJsStock,
    isSingleGdStock,
    stock,
    gdStock,
    jsStock,
    smtStock,
  }) => {
    return (
      <>
        {isSingleStock ? (
          <div className="mt-[8px]">
            <span className="text-[#69788A]">库存：</span>
            <span className="text-[#000000]">{stock}</span>
          </div>
        ) : isSingleGdStock ? (
          <div className="mt-[8px]">
            <span className="text-[#69788A]">广东仓：</span>
            <span className="text-[#000000]">{gdStock}</span>
          </div>
        ) : (
          <div className="mt-[8px] flex text-[14px] leading-[33px] rounded-[10px] overflow-hidden border-[1px] border-solid border-[#ECECEC]">
            <div className="flex-1">
              <div className="pl-[16px] text-[#69788A] bg-[#F1F1F1]">广东仓</div>
              <div className="pl-[16px] text-[#000000]">{gdStock}</div>
            </div>
            {isShowJsStock && (
              <div className="flex-1 border-l-[1px] border-solid border-[#ECECEC]">
                <div className="pl-[16px] text-[#69788A] bg-[#F1F1F1]">江苏仓</div>
                <div className="pl-[16px] text-[#000000]">{jsStock}</div>
              </div>
            )}
            {isShowSmtSubsidyInfo && (
              <div className="flex-1 border-l-[1px] border-solid border-[#ECECEC]">
                <div className="pl-[16px] text-[#69788A] bg-[#F1F1F1]">SMT仓</div>
                <div className="pl-[16px] text-[#000000]">{smtStock}</div>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const StockTableCanvasRenderer = ({
    isSingleStock,
    isShowSmtSubsidyInfo,
    isShowJsStock,
    isSingleGdStock,
    stock,
    gdStock,
    jsStock,
    smtStock,
  }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      const dpr = 2;
      const canvasWidth = 330;
      const rowHeight = 33;
      const paddingX = 16;
      const borderRadius = 10;

      const totalColumns = 1 + (isShowJsStock ? 1 : 0) + (isShowSmtSubsidyInfo ? 1 : 0);
      const warehouseWidth = canvasWidth / totalColumns;

      const canvasHeight = isSingleStock || isSingleGdStock ? 18 : 66;

      canvas.width = canvasWidth * dpr;
      canvas.height = canvasHeight * dpr;

      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;

      ctx.scale(dpr, dpr); // 缩放上下文

      // 清空画布
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      if (isSingleStock) {
        ctx.font = '12px sans-serif';
        ctx.fillStyle = '#69788A';
        ctx.fillText('库存：', 0, 15);
        ctx.fillStyle = '#000000';
        ctx.fillText(stock, 32, 15);
      } else if (isSingleGdStock) {
        ctx.font = '12px sans-serif';
        ctx.fillStyle = '#69788A';
        ctx.fillText('广东仓：', 0, 15);
        ctx.fillStyle = '#000000';
        ctx.fillText(gdStock, 48, 15);
      } else {
        // 绘制容器边框
        ctx.beginPath();
        ctx.moveTo(borderRadius, 0);
        ctx.lineTo(canvasWidth - borderRadius, 0);
        ctx.quadraticCurveTo(canvasWidth, 0, canvasWidth, borderRadius);
        ctx.lineTo(canvasWidth, canvasHeight - borderRadius);
        ctx.quadraticCurveTo(canvasWidth, canvasHeight, canvasWidth - borderRadius, canvasHeight);
        ctx.lineTo(borderRadius, canvasHeight);
        ctx.quadraticCurveTo(0, canvasHeight, 0, canvasHeight - borderRadius);
        ctx.lineTo(0, borderRadius);
        ctx.quadraticCurveTo(0, 0, borderRadius, 0);
        ctx.closePath();
        ctx.strokeStyle = '#ECECEC';
        ctx.lineWidth = 1;
        ctx.stroke();

        // 绘制表头背景
        ctx.beginPath();
        ctx.moveTo(0, borderRadius);
        ctx.quadraticCurveTo(0, 0, borderRadius, 0);
        ctx.lineTo(canvasWidth - borderRadius, 0);
        ctx.quadraticCurveTo(canvasWidth, 0, canvasWidth, borderRadius);
        ctx.lineTo(canvasWidth, rowHeight);
        ctx.lineTo(0, rowHeight);
        ctx.closePath();
        ctx.fillStyle = '#F1F1F1';
        ctx.fill();

        let xPosition = 0;

        // 广东仓
        ctx.font = '14px Arial';
        ctx.fillStyle = '#69788A';
        ctx.fillText('广东仓', xPosition + paddingX, 22);
        ctx.fillStyle = '#000000';
        ctx.fillText(gdStock, xPosition + paddingX, rowHeight + 22);
        xPosition += warehouseWidth;

        // 江苏仓
        if (isShowJsStock) {
          ctx.beginPath();
          ctx.moveTo(xPosition, 0);
          ctx.lineTo(xPosition, canvasHeight);
          ctx.stroke();

          ctx.font = '14px Arial';
          ctx.fillStyle = '#69788A';
          ctx.fillText('江苏仓', xPosition + paddingX, 22);
          ctx.fillStyle = '#000000';
          ctx.fillText(jsStock, xPosition + paddingX, rowHeight + 22);
          xPosition += warehouseWidth;
        }

        // SMT仓
        if (isShowSmtSubsidyInfo) {
          ctx.beginPath();
          ctx.moveTo(xPosition, 0);
          ctx.lineTo(xPosition, canvasHeight);
          ctx.stroke();

          ctx.font = '14px Arial';
          ctx.fillStyle = '#69788A';
          ctx.fillText('SMT仓', xPosition + paddingX, 22);
          ctx.fillStyle = '#000000';
          ctx.fillText(smtStock, xPosition + paddingX, rowHeight + 22);
        }
      }
    }, [isSingleStock, isShowSmtSubsidyInfo, isShowJsStock, isSingleGdStock, stock, gdStock, jsStock, smtStock]);

    return (
      <div className="mt-[8px]">
        <canvas ref={canvasRef} />
      </div>
    );
  };

  const StockContent = () => {
    if (!productAsyncData.isFetched) return <></>;

    try {
      const isSingleStock = !!data?.productRecord?.selfProductCode;
      const isShowSmtSubsidyInfo = data?.isShowSmtSubsidyInfo;
      const isShowJsStock = productAsyncData?.jsWarehouseStockNumber > 0;
      const isSingleGdStock = !isShowJsStock && !isShowSmtSubsidyInfo;
      const stock = productAsyncData?.productRecord?.stockNumber?.toLocaleString() || '0';
      const gdStock = productAsyncData?.gdWarehouseStockNumber?.toLocaleString() || '0';
      const jsStock = productAsyncData?.jsWarehouseStockNumber?.toLocaleString() || '0';
      const smtStock = productAsyncData?.smtStockNumber?.toLocaleString() || '0';

      return (
        <>
          <StockTableDomRenderer
            isSingleStock={isSingleStock}
            isShowSmtSubsidyInfo={isShowSmtSubsidyInfo}
            isShowJsStock={isShowJsStock}
            isSingleGdStock={isSingleGdStock}
            stock={stock}
            gdStock={gdStock}
            jsStock={jsStock}
            smtStock={smtStock}
          />
        </>
      );
    } catch (error) {
      console.error('渲染库存表失败:', error);
    }
  };

  const OverseaStockContent = () => {
    const stock = data?.productRecord?.stockNumber?.toLocaleString() || '0';
    return (
      <div className="mt-[8px]">
        <span className="text-[#69788A]">库存: </span>
        <span className="text-[#000000]">{stock}</span>
      </div>
    );
  };

  return (
    <>
      <div>
        <div className="mt-[16px] flex items-center">
          <h3 className="text-[#020202] text-[14px] font-bold">库存总量</h3>
          <span className="ml-[10px] text-[#69788A] text-[12px]">
            (单位：{productRecord?.productUnit || productRecord?.unit || '个'})
          </span>
        </div>
        {isOversea ? <OverseaStockContent /> : <StockContent />}
      </div>
    </>
  );
}

export default memo(InventoryComponents);
